import React, { useState } from 'react';
import { deleteProfileMember, setProfileMemberIsAdmin } from '@/services/profiles';
import { getInheritedMembers, removeInheritedMember } from '@/services/inheritedMembers';
import { BygrProfileDetails, ProfileMember } from '@/types';
import { createDataTable } from '@/components/DataTable';
import { CustomLink, Toast, LoadingIndicator, Select } from '@ygtechnologies/bygr-frontend-component-library';
import { useAsyncState } from '@/utils/hooks';
import MakeWatcherConfirmModal from './MakeWatcherConfirmModal';
// import theme from './ProjectDetails.module.scss';
import { Button, ConfirmButton } from '@ygtechnologies/rubus-ui';

interface Props {
    profile: BygrProfileDetails;
    reloadProfile(): void;
}
const MemberDataTable = createDataTable<ProfileMember>();

const ProfileMembers: React.FC<Props> = ({ profile, reloadProfile }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [memberTableState, setMemberTableState] = useState(MemberDataTable.getInitialState('userId'));
    const [inheritedMembers, , reloadInheritedMembers] = useAsyncState(
        () => getInheritedMembers(profile.id),
        [profile.id],
        null
    );
    const [makeWatcherFor, setMakeWatcherFor] = useState<ProfileMember | null>(null);
    const inheritedUserIds = new Set((inheritedMembers || []).map((m) => m.userId));

    const handleSetProfileMemberIsAdmin = async (profileMemberId: number, isAdmin: boolean) => {
        setIsLoading(true);
        await setProfileMemberIsAdmin(profileMemberId, isAdmin as boolean);
        await reloadProfile();
        Toast.success('Is Admin set to ' + isAdmin);
        setIsLoading(false);
    };

    const handleDeleteProfileMember = async (profileMemberId: number) => {
        try {
            setIsLoading(true);
            await deleteProfileMember(profileMemberId);
            await reloadProfile();
        } catch (e) {
            if ((e as { status?: number }).status === 400) {
                Toast.error('Error: Profilemember is connected to a project');
            } else {
                console.log(e);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleStopWatching = async (userId: number) => {
        if (
            !window.confirm(
                'Stop this user from being a global member of the tree? They will lose access to every project under this profile.'
            )
        )
            return;
        setIsLoading(true);
        try {
            await removeInheritedMember(profile.id, userId);
            await reloadInheritedMembers();
            await reloadProfile();
        } catch (e: any) {
            Toast.error(e?.message || 'Failed to remove global member.');
        } finally {
            setIsLoading(false);
        }
    };


    if (!profile) {
        return <LoadingIndicator />;
    }

    const isAdminArray = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
    ];

    return (
        <>
            <MemberDataTable
                hasPagination={false}
                data={profile.members}
                onStateChange={setMemberTableState}
                state={memberTableState}
                totalItemCount={profile.members.length}
                className="table style-3">
                <MemberDataTable.Column header="User Id" field="userId" renderer={({ value }) => value} />
                <MemberDataTable.Column header="Profile Member Id" field="profileMemberId" renderer={({ value }) => value} />
                <MemberDataTable.Column header="Email" field="email" renderer={({ value }) => value} />
                <MemberDataTable.Column
                    header="User Name"
                    field="userName"
                    renderer={({ value, row }) => <CustomLink to={`/users/${row.userId}`}>{value}</CustomLink>}
                />
                <MemberDataTable.Column
                    header="Is Admin"
                    field="isAdmin"
                    renderer={({ value, row }) => (
                        <Select
                            isDisabled={isLoading}
                            onChange={(v) => handleSetProfileMemberIsAdmin(row.profileMemberId, (v as { value: boolean }).value)}
                            value={isAdminArray.find((x) => x.value === value)}
                            options={isAdminArray}
                        />
                    )}
                />
                <MemberDataTable.Column
                    header="Global"
                    field="userId"
                    renderer={({ row }) => {
                        const isInherited = inheritedUserIds.has(row.userId);
                        return isInherited ? (
                            <Button
                                size="xs"
                                variant="tertiary"
                                loading={isLoading}
                                onClick={() => handleStopWatching(row.userId)}>
                                ✕ Stop globally
                            </Button>
                        ) : (
                            <Button
                                size="xs"
                                variant="tertiary"
                                onClick={() => setMakeWatcherFor(row)}>
                                + Add globally
                            </Button>
                        );
                    }}
                />
                <MemberDataTable.Column
                    header="Delete member"
                    field="userId"
                    renderer={({ row }) => (
                        <ConfirmButton
                            size="xs"
                            dialogTitle="Are you sure?"
                            dialogBody="Are you sure, this cannot be reverted."
                            onConfirm={() => handleDeleteProfileMember(row.profileMemberId)}
                            variant="danger"
                            confirmButton={{ variant: 'danger' }}
                            loading={isLoading}>
                            Delete profile member
                        </ConfirmButton>
                    )}
                />
            </MemberDataTable>
            {makeWatcherFor && (
                <MakeWatcherConfirmModal
                    isOpen={!!makeWatcherFor}
                    profileId={profile.id}
                    profileName={profile.name}
                    userId={makeWatcherFor.userId}
                    userDisplayName={makeWatcherFor.userName}
                    onClose={() => setMakeWatcherFor(null)}
                    onSuccess={async () => {
                        await reloadInheritedMembers();
                        await reloadProfile();
                        Toast.success('Added as global member.');
                    }}
                />
            )}
        </>
    );
};

export default ProfileMembers;
